/**
 * TraceMetrics - Overview metrics with enhanced stat cards and time-series charts
 */

import { useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { Activity, Hash, Coins, Clock, AlertTriangle } from 'lucide-react';
import { useTraceStats, useTraceMetrics } from '../hooks/useTraces';
import type { TraceStatsResponse, StatItem, MetricSeries } from '@/types/traces';
import { useI18n } from '@/i18n';

// ============================================================================
// Helpers
// ============================================================================

function findStat(stats: StatItem[], name: string): StatItem | undefined {
  return stats.find(s => s.name === name);
}

/** Format large numbers: 377600000 → "377.6M", 87384 → "87,385" */
function formatLargeNumber(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  return n.toLocaleString();
}

/** Format ms duration to human-readable */
function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

/** Parse ISO date to short label like "03-07" */
function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Get date range string like "2026-03-06 ~ 03-13" */
function getDateRangeLabel(data: Array<{ time: string }>): string {
  if (data.length === 0) return '';
  const first = new Date(data[0].time);
  const last = new Date(data[data.length - 1].time);
  const fy = first.getFullYear();
  const fm = String(first.getMonth() + 1).padStart(2, '0');
  const fd = String(first.getDate()).padStart(2, '0');
  const lm = String(last.getMonth() + 1).padStart(2, '0');
  const ld = String(last.getDate()).padStart(2, '0');
  return `${fy}-${fm}-${fd} ~ ${lm}-${ld}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function TraceMetrics() {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const { data: statsData, isLoading: statsLoading } = useTraceStats();
  const { data: traceCountData, isLoading: countLoading } = useTraceMetrics({ metric_type: 'TRACE_COUNT', interval: 'DAILY' });
  const { data: durationData, isLoading: durationLoading } = useTraceMetrics({ metric_type: 'DURATION', interval: 'DAILY' });
  const { data: tokenData, isLoading: tokenLoading } = useTraceMetrics({ metric_type: 'TOKEN_USAGE', interval: 'DAILY' });

  const statItems = (statsData as TraceStatsResponse)?.stats ?? [];

  // Extract stats
  const traceCount = findStat(statItems, 'trace_count')?.value as number | undefined;
  const errorCount = findStat(statItems, 'error_count')?.value as number | undefined;
  const totalCostSum = findStat(statItems, 'total_estimated_cost_sum')?.value as number | undefined;
  const totalTokensSum = findStat(statItems, 'usage_sum.total_tokens')?.value as number | undefined;
  const durationStat = findStat(statItems, 'duration');
  const durationObj = typeof durationStat?.value === 'object' ? durationStat.value as Record<string, number> : null;
  const durationP50 = durationObj?.p50;
  const durationP90 = durationObj?.p90;
  const durationP99 = durationObj?.p99;

  // Derived stats
  const avgCost = traceCount && totalCostSum ? totalCostSum / traceCount : undefined;
  const avgTokens = traceCount && totalTokensSum ? Math.round(totalTokensSum / traceCount) : undefined;

  // Chart data: Daily trace count
  const traceCountChartData = useMemo(() => {
    const series = traceCountData?.results?.find((s: MetricSeries) => s.name === 'traces');
    if (!series?.data) return [];
    return series.data.map(p => ({ date: formatDateLabel(p.time), value: p.value, time: p.time }));
  }, [traceCountData]);

  // Chart data: Daily duration distribution (P50, P90, P99)
  // Note: Opik metrics API returns all series with identical time points, so index-based alignment is safe.
  const durationChartData = useMemo(() => {
    if (!durationData?.results?.length) return [];
    const p50s = durationData.results.find((s: MetricSeries) => s.name === 'duration.p50');
    const p90s = durationData.results.find((s: MetricSeries) => s.name === 'duration.p90');
    const p99s = durationData.results.find((s: MetricSeries) => s.name === 'duration.p99');
    const base = p50s?.data || p90s?.data || p99s?.data || [];
    return base.map((p, i) => ({
      date: formatDateLabel(p.time),
      time: p.time,
      p50: p50s?.data[i]?.value != null ? +(p50s.data[i].value / 1000).toFixed(1) : null,
      p90: p90s?.data[i]?.value != null ? +(p90s.data[i].value / 1000).toFixed(1) : null,
      p99: p99s?.data[i]?.value != null ? +(p99s.data[i].value / 1000).toFixed(1) : null,
    }));
  }, [durationData]);

  // Chart data: Daily token usage
  const tokenChartData = useMemo(() => {
    if (!tokenData?.results?.length) return [];
    const prompt = tokenData.results.find((s: MetricSeries) => s.name === 'prompt');
    const completion = tokenData.results.find((s: MetricSeries) => s.name === 'completion');
    const total = tokenData.results.find((s: MetricSeries) => s.name === 'total');
    const base = total?.data || prompt?.data || [];
    return base.map((p, i) => ({
      date: formatDateLabel(p.time),
      time: p.time,
      prompt: prompt?.data[i]?.value ?? 0,
      completion: completion?.data[i]?.value ?? 0,
      total: total?.data[i]?.value ?? 0,
    }));
  }, [tokenData]);

  if (statsLoading) {
    return (
      <div className="p-6 space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">{t.metricsTitle}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
              <div className="h-8 w-8 rounded-lg bg-gray-100 mb-3" />
              <div className="h-3 w-16 bg-gray-100 rounded mb-2" />
              <div className="h-6 w-24 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (statItems.length === 0) {
    return <div className="p-6 text-gray-400 text-sm">{t.noStats}</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">{t.metricsTitle}</h3>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          iconBg="bg-blue-50 text-blue-500"
          label={t.statTraceCount}
          value={traceCount != null ? traceCount.toLocaleString() : '-'}
          sub={errorCount != null && errorCount > 0 ? t.statErrorSub.replace('{count}', String(errorCount)) : undefined}
          subColor="text-red-500"
        />
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          iconBg="bg-green-50 text-green-500"
          label={t.statDuration}
          value={durationP50 != null
            ? `${formatDuration(durationP50)} / ${durationP90 != null ? formatDuration(durationP90) : '-'}`
            : '-'}
          sub={durationP99 != null ? `P99: ${formatDuration(durationP99)}` : undefined}
        />
        <StatCard
          icon={<Coins className="w-5 h-5" />}
          iconBg="bg-yellow-50 text-yellow-600"
          label={t.statCost}
          value={totalCostSum != null ? `$${totalCostSum.toFixed(2)}` : '-'}
          sub={avgCost != null ? t.statAvgCost.replace('{cost}', avgCost.toFixed(4)) : undefined}
        />
        <StatCard
          icon={<Hash className="w-5 h-5" />}
          iconBg="bg-purple-50 text-purple-500"
          label={t.statTokens}
          value={totalTokensSum != null ? formatLargeNumber(totalTokensSum) : '-'}
          sub={avgTokens != null ? t.statAvgTokens.replace('{count}', avgTokens.toLocaleString()) : undefined}
        />
      </div>

      {/* Error banner */}
      {errorCount != null && errorCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg text-sm text-red-700">
          <AlertTriangle className="w-4 h-4" />
          <span>{t.errorBanner.replace('{count}', String(errorCount))}</span>
        </div>
      )}

      {/* Daily Trace Count Bar Chart */}
      <ChartCard
        title={t.chartTraceCount}
        dateRange={getDateRangeLabel(traceCountChartData)}
        loading={countLoading}
        empty={traceCountChartData.length === 0}
        loadingText={t.loading}
        emptyText={t.noData}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={traceCountChartData} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(value) => [Number(value).toLocaleString(), t.tooltipTraces]}
            />
            <Bar dataKey="value" fill="#93c5fd" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 11, fill: '#6b7280' }} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Daily Duration Distribution Line Chart */}
      <ChartCard
        title={t.chartDuration}
        dateRange={getDateRangeLabel(durationChartData)}
        loading={durationLoading}
        empty={durationChartData.length === 0}
        loadingText={t.loading}
        emptyText={t.noData}
        legend={
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block" /> P50</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-rose-400 inline-block" /> P90</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-gray-400 inline-block" /> P99</span>
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={durationChartData} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} unit="s" />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(value, name) => [`${Number(value)}s`, String(name).toUpperCase()]}
            />
            <Line type="monotone" dataKey="p50" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="p50" />
            <Line type="monotone" dataKey="p90" stroke="#fb7185" strokeWidth={2} dot={{ r: 3 }} name="p90" />
            <Line type="monotone" dataKey="p99" stroke="#9ca3af" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" name="p99" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Daily Token Usage Stacked Bar Chart */}
      <ChartCard
        title={t.chartTokenUsage}
        dateRange={getDateRangeLabel(tokenChartData)}
        loading={tokenLoading}
        empty={tokenChartData.length === 0}
        loadingText={t.loading}
        emptyText={t.noData}
        legend={
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" /> {t.tooltipPrompt}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-400 inline-block" /> {t.tooltipCompletion}</span>
          </div>
        }
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={tokenChartData} margin={{ top: 20, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatLargeNumber(v)} />
            <Tooltip
              contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              formatter={(value, name) => [Number(value).toLocaleString(), String(name) === 'prompt' ? t.tooltipPrompt : t.tooltipCompletion]}
            />
            <Bar dataKey="prompt" stackId="tokens" fill="#60a5fa" radius={[0, 0, 0, 0]} name="prompt" />
            <Bar dataKey="completion" stackId="tokens" fill="#34d399" radius={[4, 4, 0, 0]} name="completion" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <p className="text-xs text-gray-400">
        {t.dataSource}
      </p>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function StatCard({
  icon, iconBg, label, value, sub, subColor,
}: {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  value: string;
  sub?: string;
  subColor?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${iconBg}`}>
        {icon}
      </div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className={`text-xs mt-0.5 ${subColor || 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
}

function ChartCard({
  title, dateRange, loading, empty, legend, children, loadingText, emptyText,
}: {
  title: string;
  dateRange?: string;
  loading?: boolean;
  empty?: boolean;
  legend?: React.ReactNode;
  children: React.ReactNode;
  loadingText?: string;
  emptyText?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <div className="flex items-center gap-4">
          {legend}
          {dateRange && <span className="text-xs text-gray-400">{dateRange}</span>}
        </div>
      </div>
      {loading ? (
        <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">{loadingText ?? 'Loading...'}</div>
      ) : empty ? (
        <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm">{emptyText ?? 'No data'}</div>
      ) : (
        children
      )}
    </div>
  );
}
